import React, { ComponentProps } from 'react';
import { VideoDefaultView } from './views/ContentVideo';
import { DefaultView, LazyEditView, Views } from '../../types/BlockWithViews';
import { useSonTranslation } from '../../translation/useSonTranslation';
import { useWidgetContext } from '../../_lib/components/Widget';
import { useContentWrapperContext } from '../../components/blocks/ContentWrapper/ContentWrapperContext';
import { asFetchableBlock, useBlockContext, useRefreshBlock } from '../../_lib/BlockFetcher';
import {
  BlockInteractions,
  Views as BlockInteractionsViews,
  DISPLAY_TYPE,
  createButtonObject,
} from '../../components/organisms/BlockInteractions';
import { Icons } from '../../components/atoms/SVG';
import { VideoUploadEditView } from './views/VideoUploadForm';
import { Namespace } from '../../translation/namespace';
import { BlockType } from '../../types/api/ElementType';
import { useContentWrapperDndContext } from '../../components/blocks/ContentWrapper/ContentWrapperDndProvider';

type VideoProps = Views<DefaultView<any> & LazyEditView>;

function Video({ activeView, views }: VideoProps) {
  const { t } = useSonTranslation('VideoBlock');
  const { header } = useWidgetContext();

  const contentWrapperProps = useContentWrapperContext();
  const contentWrapperDndProps = useContentWrapperDndContext();

  const { uuid, elementType } = useBlockContext();
  const { refresh, refreshCount } = useRefreshBlock();

  const blockInteractionButtons = () => {
    const editButton = createButtonObject(
      BlockInteractionsViews.EDIT,
      Icons.EDIT_PENCIL,
      `${Namespace.VideoBlock}:edit`,
      DISPLAY_TYPE.COLLAPSIBLE,
      <VideoUploadEditView {...views.edit} />,
    );

    const eraseButton = createButtonObject(
      BlockInteractionsViews.DELETE,
      Icons.ERASER,
      `${Namespace.VideoBlock}:delete`,
      DISPLAY_TYPE.MODAL,
      null,
    );

    return { ...editButton, ...eraseButton };
  };

  return (
    <BlockInteractions
      contentWrapperProps={contentWrapperProps}
      contentWrapperDndProps={contentWrapperDndProps}
      headline={{
        text: t('headline'),
        // if header is set, we already have a parent h2, so we use h3 here
        hierarchy: header ? 3 : 2,
        visible: true,
      }}
      alwaysShowInfo={false}
      views={views}
      activeView={activeView}
      buttons={blockInteractionButtons()}
      refreshBlock={refresh}
      refreshCount={refreshCount}
      uuid={uuid}
      elementType={elementType}
    >
      <VideoDefaultView {...views.default} />
    </BlockInteractions>
  );
}

export const VideoBlock = asFetchableBlock(Video, BlockType.video);
export type VideoBlock = ComponentProps<typeof VideoBlock>;
